import { useEffect, useState } from 'react';
import { log } from '../../app/logger';
import { adjustBoundaries, loadTriageContext, previewBoundaries } from '../../app/triage';
import type { TriageContext } from '../../app/triage';
import type { MatchAnchors } from '../../types';

/**
 * B-18 — **Adjust match clock** (design spec §2.4; SES-8/§10.1), B's rendering of the engine's
 * most destructive control.
 *
 * ── Nothing here computes a minute ──────────────────────────────────────────
 *
 * The preview is `app/triage.previewBoundaries`, which is `domain/commands.previewAdjustment` —
 * **the same call `handleAdjustBoundaries` makes to build its ops** — and the commit is
 * `app/triage.adjustBoundaries`. Both are A's facade functions, reused as-is; what is B's here is
 * the surface. If this file derived a minute of its own, the number the user approves and the
 * number that commits could differ, which on this control is the whole risk.
 *
 * ── Why it loads its own context, and only when opened ──────────────────────
 *
 * `loadTriageContext` materialises every Observation and MatchNote of the match so SES-8 can
 * preview a re-derivation. `app/triage.ts` makes the argument itself one surface over: a screen's
 * cost does not belong on a control nobody may tap. The record therefore does not carry these rows
 * — this pane fetches them at the moment it opens, which is also the moment they must be current.
 *
 * ── Tapping, never typing (LIVE-9) ──────────────────────────────────────────
 *
 * Whole-minute nudges, the granularity SES-4 derives in anyway: a boundary moved by twenty seconds
 * changes no minute, so a time field would offer precision the product cannot express — and would
 * open a keyboard on a screen whose whole purpose is checking numbers against each other.
 */

/** The four anchors, in wall-clock order, with the labels the user reads. */
const FIELDS = [
  { key: 'kickoffTs', label: 'Kick-off' },
  { key: 'halfTs', label: 'Half time' },
  { key: 'kickoff2Ts', label: 'Second-half kick-off' },
  { key: 'fullTimeTs', label: 'Full time' },
] as const;

type AnchorSet = Omit<MatchAnchors, 'halfLengthMin'>;

const MINUTE_MS = 60_000;
/** One minute is SES-4's own granularity; five is a mis-tapped whistle. */
const NUDGES = [-5, -1, 1, 5] as const;

const MODULE = 'ui-b/record';

export interface BoundaryPaneBProps {
  readonly matchId: string;
  /** A committed adjustment — the record re-reads (every minute on it just moved). */
  readonly onCommitted: () => void;
  readonly onClose: () => void;
}

export function BoundaryPaneB({ matchId, onCommitted, onClose }: BoundaryPaneBProps) {
  const [context, setContext] = useState<TriageContext | null>(null);
  const [anchors, setAnchors] = useState<AnchorSet | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    loadTriageContext(matchId).then(
      (loaded) => {
        if (!live) return;
        setContext(loaded);
        setAnchors({
          kickoffTs: loaded.match.kickoffTs,
          halfTs: loaded.match.halfTs,
          kickoff2Ts: loaded.match.kickoff2Ts,
          fullTimeTs: loaded.match.fullTimeTs,
        });
      },
      (cause: unknown) => {
        log.error(MODULE, 'the boundary context could not be read', {
          matchId,
          message: cause instanceof Error ? cause.message : String(cause),
        });
        if (live) setFailed(true);
      },
    );
    return () => {
      live = false;
    };
  }, [matchId]);

  if (failed) {
    return (
      <section className="rec-clock" data-testid="rec-clock" data-phase="failed">
        <p className="rec-empty" role="alert">
          The match clock could not be read. The log has the detail.
        </p>
        <button type="button" className="rec-secondary" data-testid="rec-clock-close" onClick={onClose}>
          Back to the record
        </button>
      </section>
    );
  }

  if (context === null || anchors === null) {
    return (
      <section className="rec-clock" data-testid="rec-clock" data-phase="loading">
        <p className="rec-empty">Reading the clock…</p>
      </section>
    );
  }

  const original: AnchorSet = {
    kickoffTs: context.match.kickoffTs,
    halfTs: context.match.halfTs,
    kickoff2Ts: context.match.kickoff2Ts,
    fullTimeTs: context.match.fullTimeTs,
  };
  const result = previewBoundaries(context, anchors);
  const moved = FIELDS.filter(({ key }) => anchors[key] !== original[key]);

  return (
    <section
      className="rec-clock"
      data-testid="rec-clock"
      data-phase="ready"
      data-changed={String(result.changed)}
      data-moved={String(moved.length)}
      aria-label="Adjust match clock"
    >
      <div className="rec-bar">
        <h2 className="rec-section__title">Adjust match clock</h2>
        <button type="button" className="rec-secondary" data-testid="rec-clock-close" onClick={onClose}>
          Back
        </button>
      </div>
      <p className="rec-note">
        Moving a boundary re-derives the minute of every observation and note on this match. Nothing
        changes until you commit.
      </p>

      {notice !== null && (
        <p className="rec-notice" role="alert" data-testid="rec-clock-notice">
          {notice}
        </p>
      )}

      <ul className="rec-anchors">
        {FIELDS.map(({ key, label }) => {
          const value = anchors[key];
          return (
            <li className="rec-anchor" key={key} data-testid={`rec-anchor-${key}`} data-field={key}>
              <span className="rec-anchor__label">{label}</span>
              <span className="rec-anchor__value" data-testid={`rec-anchor-value-${key}`}>
                {value === null ? 'not recorded' : clockLabel(value)}
                {value !== null && original[key] !== null && value !== original[key] && (
                  <em className="rec-anchor__delta" data-testid={`rec-anchor-delta-${key}`}>
                    {' '}
                    {formatDelta(value - (original[key] ?? 0))}
                  </em>
                )}
              </span>
              <span className="rec-nudges">
                {NUDGES.map((minutes) => (
                  <button
                    type="button"
                    key={minutes}
                    className="rec-nudge"
                    data-testid={`rec-nudge-${key}-${String(minutes)}`}
                    /* A boundary the match never recorded cannot be moved — and cannot be
                       *created* here either: `handleAdjustBoundaries` refuses to erase one, and
                       inventing one would fabricate a half that was never played. */
                    disabled={busy || value === null}
                    onClick={() => {
                      setAnchors((current) =>
                        current === null
                          ? current
                          : { ...current, [key]: (current[key] ?? 0) + minutes * MINUTE_MS },
                      );
                    }}
                  >
                    {minutes > 0 ? `+${String(minutes)}` : String(minutes)}
                  </button>
                ))}
              </span>
            </li>
          );
        })}
      </ul>

      <p className="rec-preview" data-testid="rec-clock-preview">
        {result.range === null
          ? 'Nothing on this match derives a minute yet, so nothing would move.'
          : `${String(result.changed)} of ${String(result.observations.length + result.notes.length)} entries would move. Minutes would run ${String(result.range.min)}′–${String(result.range.max)}′.`}
      </p>

      <div className="rec-row">
        <button
          type="button"
          className="rec-primary"
          data-testid="rec-clock-commit"
          disabled={busy || moved.length === 0}
          onClick={() => {
            setBusy(true);
            setNotice(null);
            void adjustBoundaries(matchId, anchors)
              .then((committed) => {
                if (!committed.ok) {
                  setNotice(committed.message);
                  return;
                }
                onCommitted();
              })
              .catch((cause: unknown) => {
                const message = cause instanceof Error ? cause.message : String(cause);
                log.error(MODULE, 'the boundary adjustment could not be committed', {
                  matchId,
                  message,
                });
                setNotice(message);
              })
              .finally(() => {
                setBusy(false);
              });
          }}
        >
          Commit the new boundaries
        </button>
        <button
          type="button"
          className="rec-secondary"
          data-testid="rec-clock-reset"
          disabled={busy || moved.length === 0}
          onClick={() => {
            setAnchors(original);
          }}
        >
          Undo my changes
        </button>
      </div>
    </section>
  );
}

/** Wall-clock time, local, to the minute — the only form the user recognises on a touchline. */
function clockLabel(ts: number): string {
  return new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function formatDelta(deltaMs: number): string {
  const minutes = Math.round(deltaMs / MINUTE_MS);
  return minutes > 0 ? `+${String(minutes)} min` : `${String(minutes)} min`;
}
